import { useEffect, useMemo, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Filter from "./FilterBranch";
import { FormButton } from "components/Common/Buttons";
import { COLUMNS_DATA, FILTER_DEFINATIONS } from "./config";
import Table from "components/Common/Table/index";
import usePagination from "hooks/usePagination";
import useFilterHook from "hooks/useFilter";
import { DEFAULT_TOTAL_RECORDS, STALE_TIME } from "utils/constants";
import { fetchTatReports } from "services/tatReport";
import { useDispatch, useSelector } from "react-redux";
import {
  getDoctorList,
  getFranchiseTypesList,
  getInvestigationList,
  getZoneList,
  getDepartmentList,
  getStatusList,
  getPackageList,
  getFranchiseList,
  getLabDepartmentList,
} from "../../redux/action/drop_list_action";

const CSV_COLUMN_MAP = [
  { header: "S.No", accessor: "_index" },
  { header: "Appointment ID", accessor: "appointment_id" },
  { header: "Barcode No", accessor: "barcode_no" },
  { header: "Patient Name", accessor: "patient_name" },
  { header: "Age", accessor: "patient_age" },
  { header: "Franchise Name", accessor: "franchise_name" },
  { header: "Franchise Code", accessor: "franchise_code" },
  { header: "Investigation", accessor: "investigation_name" },
  { header: "Department", accessor: "department_name" },
  { header: "Sample Collection Date", accessor: "sample_collection_date" },
  { header: "Sample Receive Date", accessor: "sample_receive_date" },
  { header: "Result Entered Date", accessor: "result_entered_date" },
  { header: "Result Entered By", accessor: "result_entered_by_name" },
  { header: "Approved Date", accessor: "approved_date" },
  { header: "Approved By", accessor: "approved_by_name" },
  { header: "Delivery Time", accessor: "delivery_time" },
  { header: "Actual TAT", accessor: "actual_tat" },
  { header: "Deviation", accessor: "deviation" },
];

const EPOCH_FIELDS = [
  "sample_collection_date",
  "sample_receive_date",
  "result_entered_date",
  "approved_date",
  "delivery_time",
];

const formatEpochForCSV = (value) => {
  if (!value) return "-";
  const date = new Date(value * 1000);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const mins = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${mins}`;
};

const formatAgeForCSV = (value) => {
  if (!value || typeof value !== "object") return "-";
  const parts = [];
  if (value.year) parts.push(`${value.year}Y`);
  if (value.month) parts.push(`${value.month}M`);
  if (value.days) parts.push(`${value.days}D`);
  return parts.length ? parts.join(" ") : "-";
};

const TatReport = () => {
  const [isAddBranchOpen, setIsAddBranchOpen] = useState(false);
  const [rowDataForEdit, setRowDataForEdit] = useState(null);
  const [isDelete, setIsDelete] = useState(-1);
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);

  const [searchedFilters, setSearchedFilters] = useState(null);

  const dispatch = useDispatch();

  const [isSuperFranchise] = useState(() =>
    Number(localStorage.getItem("is_super_franchise")),
  );
  const [isFranchiseEmployee] = useState(() =>
    Number(localStorage.getItem("is_franchise_employee")),
  );
  const [isSubFranchise] = useState(() =>
    Number(localStorage.getItem("sub_franchise")),
  );

  const {
    investigation_list,
    zone_list,
    package_list,
    franchise_list,
    lab_department_list,
  } = useSelector((state) => state.dropListReducer);

  useEffect(() => {
    dispatch(getPackageList());
    dispatch(getInvestigationList());
    dispatch(getFranchiseTypesList());
    dispatch(getDoctorList());
    dispatch(getZoneList());
    dispatch(getDepartmentList());
    dispatch(getStatusList());
    dispatch(getFranchiseList());
    dispatch(getLabDepartmentList());
  }, []);

  const {
    currentPage,
    itemPerPage,
    disablePrev,
    disableNext,
    disableFirst,
    disableLast,
    totalPages,
    updateCurrentPage,
    updateTotalRecords,
  } = usePagination();

  const { filters, updateFilter, updateMultipleFilters } = useFilterHook(
    FILTER_DEFINATIONS,
    updateCurrentPage,
  );

  const handleSearchSubmit = useCallback(
    (newFilters) => {
      updateMultipleFilters(newFilters);
      setSearchedFilters(newFilters);
      updateCurrentPage(1);
    },
    [updateMultipleFilters, updateCurrentPage],
  );

  const editBranch = useCallback((row) => {
    setRowDataForEdit(row);
    setIsAddBranchOpen(true);
  }, []);

  const deleteRow = useCallback((row) => {
    setIsDelete(row.branch_id);
  }, []);

  const {
    data: branchData,
    isLoading,
    refetch: refetchBranches,
  } = useQuery(
    ["fetch-tat-report", currentPage, searchedFilters],
    () => fetchTatReports(currentPage, searchedFilters),
    {
      enabled: searchedFilters !== null,
      keepPreviousData: false,
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res?.data?.additional_data?.total_count);
      },
      select: (res) => {
        const response = res?.data?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        }
        return res;
      },
      onError: (res) => {
        console.error("Error fetching TAT report:", res);
      },
    },
  );

  const handleDownloadCSV = useCallback(async () => {
    try {
      setIsDownloadingCSV(true);

      const res = await fetchTatReports(1, { ...searchedFilters, no_limit: 1 });
      const tableData = res?.data?.data || [];

      if (!tableData.length) {
        alert("Download ke liye koi data nahi hai.");
        return;
      }

      const headerRow = CSV_COLUMN_MAP.map((col) => `"${col.header}"`).join(
        ",",
      );

      const dataRows = tableData.map((row, index) => {
        const rowWithIndex = { ...row, _index: index + 1 };
        return CSV_COLUMN_MAP.map(({ accessor }) => {
          let val = rowWithIndex[accessor] ?? "";

          if (accessor === "patient_age") {
            val = formatAgeForCSV(val);
          } else if (EPOCH_FIELDS.includes(accessor) && val) {
            val = formatEpochForCSV(val);
          }

          return `"${String(val).replace(/"/g, '""')}"`;
        }).join(",");
      });

      const csvContent = [headerRow, ...dataRows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `tat_report_full.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("CSV download error:", error);
      alert("CSV download mein kuch gadbad ho gayi.");
    } finally {
      setIsDownloadingCSV(false);
    }
  }, [searchedFilters]);

  const onClose = useCallback(
    (refetch = false) => {
      if (refetch) refetchBranches();
      setIsAddBranchOpen(false);
      setRowDataForEdit(null);
    },
    [refetchBranches],
  );

  const onCloseDelete = useCallback(
    (refetch = false) => {
      if (refetch) refetchBranches();
      setIsDelete(-1);
    },
    [refetchBranches],
  );

  const columns = useMemo(() => {
    return COLUMNS_DATA({
      deleteRow,
      editBranch,
      isSuperFranchise,
      isFranchiseEmployee,
      isSubFranchise,
      filters,
    });
  }, [
    deleteRow,
    editBranch,
    isSuperFranchise,
    isFranchiseEmployee,
    isSubFranchise,
    filters.is_active,
  ]);

  const loadNextPage = useCallback(() => {
    updateCurrentPage(currentPage + 1);
  }, [currentPage, updateCurrentPage]);

  const loadPreviousPage = useCallback(() => {
    updateCurrentPage(currentPage - 1);
  }, [currentPage, updateCurrentPage]);

  const loadFirstPage = useCallback(() => {
    if (currentPage !== 1) {
      updateCurrentPage(1);
    }
  }, [currentPage, updateCurrentPage]);

  const loadLastPage = useCallback(() => {
    if (currentPage !== totalPages) {
      updateCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, updateCurrentPage]);

  // if (isLoading) {
  //   return <Loader isLoading={isLoading} />;
  // }

  return (
    <div className="container-fluid mb-3">
      <div className="box mb-4">
        <div
          className="boxHeader"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1>TAT Report</h1>
          <FormButton
            onClick={handleDownloadCSV}
            button_name={isDownloadingCSV ? "Downloading..." : "Download CSV"}
            disabled={isDownloadingCSV}
          />
        </div>

        <div className="left d-lg-flex align-items-center">
          <Filter
            updateFilter={updateFilter}
            filters={filters}
            updateMultipleFilters={handleSearchSubmit}
            investigation_list={investigation_list}
            package_list={package_list}
            franchise_list={franchise_list}
            lab_department_list={lab_department_list}
          />
        </div>

        <div className="boxBody">
          {searchedFilters === null ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 20px",
                color: "#6c757d",
                fontSize: "16px",
              }}
            >
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔍</div>
              <p>
                Select a filter and click the <strong>Search</strong> button
              </p>
            </div>
          ) : (
            <Table
              data={branchData?.data?.data || []}
              isLoading={isLoading}
              tableColumns={columns}
              currentPage={currentPage}
              totalPages={totalPages}
              itemPerPage={itemPerPage}
              disablePrev={disablePrev}
              disableNext={disableNext}
              disableFirst={disableFirst}
              disableLast={disableLast}
              loadFirstPage={loadFirstPage}
              loadLastPage={loadLastPage}
              totalRecords={5}
              loadNextPage={loadNextPage}
              loadPreviousPage={loadPreviousPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TatReport;
